import moment from 'moment';

/**
 * Month view data helpers — pure, no React, so they can be unit tested.
 *
 * The month view reads the same per-day shape the week view gets from
 * normalizeDiaryWeekResponse: { [date]: DiaryEvent[] } plus
 * { [date]: { isWorking, startMinutes, endMinutes } }.
 */

export const WEEKDAY_LABELS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
export const MAX_DAY_ITEMS = 3;

/**
 * The day to land on when moving to the month holding `date`: today in the
 * current month, otherwise the 1st. Back in the day view, that's the day shown.
 */
export function monthLandingDay(date, todayKey = moment().format('YYYY-MM-DD')) {
  const first = moment(date).startOf('month');
  return first.format('YYYY-MM') === todayKey.slice(0, 7) ? todayKey : first.format('YYYY-MM-DD');
}

/** 'YYYY-MM' for any date-ish input. */
export function monthKeyOf(date) {
  return moment(date).format('YYYY-MM');
}

/**
 * The calendar grid for the month containing `anchorDate`: Monday-first,
 * padded at both ends to whole weeks, so the fetch range is the grid range.
 *
 * @returns {{ monthKey, title, startDate, endDate, weeks: Day[][], days: Day[] }}
 *   Day = { key, dayNum, weekday, inMonth, isToday, isWeekend }
 */
export function buildMonthGrid(anchorDate, todayKey = moment().format('YYYY-MM-DD')) {
  const first = moment(anchorDate, 'YYYY-MM-DD').startOf('month');
  const last = first.clone().endOf('month');
  const gridStart = first.clone().startOf('isoWeek');
  const gridEnd = last.clone().endOf('isoWeek').startOf('day');

  const days = [];
  const cursor = gridStart.clone();
  while (!cursor.isAfter(gridEnd, 'day')) {
    const key = cursor.format('YYYY-MM-DD');
    const weekday = cursor.isoWeekday(); // 1 = Mon … 7 = Sun
    days.push({
      key,
      dayNum: cursor.date(),
      weekday,
      inMonth: cursor.month() === first.month(),
      isToday: key === todayKey,
      isWeekend: weekday >= 6,
    });
    cursor.add(1, 'day');
  }

  const weeks = [];
  for (let i = 0; i < days.length; i += 7) weeks.push(days.slice(i, i + 7));

  return {
    monthKey: first.format('YYYY-MM'),
    title: first.format('MMMM YYYY'),
    startDate: gridStart.format('YYYY-MM-DD'),
    endDate: gridEnd.format('YYYY-MM-DD'),
    weeks,
    days,
  };
}

/** Anchor date ('YYYY-MM-01') for the month `delta` months from `anchorDate`. */
export function shiftMonth(anchorDate, delta) {
  return moment(anchorDate, 'YYYY-MM-DD').startOf('month').add(delta, 'months').format('YYYY-MM-DD');
}

const LEAVE_LABELS = { holiday: 'Holiday', sick: 'Sick leave', other: 'Time off' };

/** What a day of leave is called on the calendar. */
export function holidayLabel(hol) {
  const title = (hol && hol.title ? String(hol.title) : '').trim();
  if (title && title.toLowerCase() !== 'leave') return title;
  return LEAVE_LABELS[hol && hol.leaveType] || 'Holiday';
}

function firstName(name) {
  const n = (name || '').trim();
  return n ? n.split(/\s+/)[0] : '';
}

/** Total minutes covered by a set of [start, end) intervals, overlaps counted once. */
export function unionMinutes(intervals) {
  const sorted = intervals
    .filter(([s, e]) => e > s)
    .sort((a, b) => a[0] - b[0]);
  let total = 0;
  let curS = null;
  let curE = null;
  sorted.forEach(([s, e]) => {
    if (curE === null || s > curE) {
      if (curE !== null) total += curE - curS;
      curS = s;
      curE = e;
    } else if (e > curE) {
      curE = e;
    }
  });
  if (curE !== null) total += curE - curS;
  return total;
}

/**
 * Summarise one stylist-day for a calendar cell.
 *
 * - A booking split into service segments (processing gaps) is ONE booking.
 * - Items are bookings and blocked time (diary events), in time order. Rota
 *   breaks repeat every day, so they count against the shift, not as items.
 * - Utilisation = booked minutes (overlaps once, inside the shift) ÷ shift
 *   minutes less breaks. Null when there is no shift that day.
 *
 * @returns {{ items, moreCount, bookingCount, eventCount, bookedMinutes,
 *   shiftMinutes, utilisation, isHoliday, holidayLabel, isOff, isWorking }}
 */
export function summariseDay(events = [], info = {}, maxItems = MAX_DAY_ITEMS) {
  const bookingsById = new Map();
  const blocked = [];
  const breaks = [];
  const holidays = [];

  events.forEach((evt) => {
    if (!evt) return;
    if (evt.type === 'booking') {
      const id = String(evt.bookingId || evt.chainId || evt.id);
      const prev = bookingsById.get(id);
      if (!prev) {
        bookingsById.set(id, { first: evt, segments: [evt], start: evt.startMinutes, end: evt.endMinutes });
      } else {
        prev.segments.push(evt);
        if (evt.startMinutes < prev.start) { prev.start = evt.startMinutes; prev.first = evt; }
        if (evt.endMinutes > prev.end) prev.end = evt.endMinutes;
      }
    } else if (evt.type === 'event') {
      blocked.push(evt);
    } else if (evt.type === 'break') {
      breaks.push(evt);
    } else if (evt.type === 'holiday') {
      holidays.push(evt);
    }
  });

  const bookingItems = Array.from(bookingsById.values()).map(({ first, segments, start, end }) => {
    const svc = (first.services && first.services[0]) || null;
    const serviceName = (first.services || []).map(s => s.serviceName).filter(Boolean).join(', ') || first.title || '';
    return {
      kind: 'booking',
      id: String(first.bookingId || first.id),
      startMinutes: start,
      endMinutes: end,
      clientFirstName: firstName(first.clientName),
      serviceName,
      colour: (svc && svc.diaryColour) || first.diaryColour || null,
      event: first,
      segments,
    };
  });

  const eventItems = blocked.map(evt => ({
    kind: 'event',
    id: String(evt.eventId || evt.id),
    startMinutes: evt.startMinutes,
    endMinutes: evt.endMinutes,
    title: evt.title || 'Event',
    emoji: evt.emoji || null,
    colour: evt.diaryColour || null,
    event: evt,
  }));

  const all = bookingItems.concat(eventItems).sort((a, b) =>
    (a.startMinutes - b.startMinutes) || (a.kind === b.kind ? 0 : a.kind === 'booking' ? -1 : 1));

  const isHoliday = holidays.length > 0;
  const isWorking = !!info.isWorking && info.startMinutes != null && info.endMinutes != null;

  let shiftMinutes = 0;
  let bookedMinutes = 0;
  let utilisation = null;
  if (isWorking && info.endMinutes > info.startMinutes) {
    const sS = info.startMinutes;
    const sE = info.endMinutes;
    const clip = ([s, e]) => [Math.max(s, sS), Math.min(e, sE)];
    const breakMinutes = unionMinutes(breaks.map(b => clip([b.startMinutes, b.endMinutes])));
    shiftMinutes = Math.max(0, sE - sS - breakMinutes);
    const bookingIntervals = [];
    bookingItems.forEach(b => b.segments.forEach(seg => bookingIntervals.push(clip([seg.startMinutes, seg.endMinutes]))));
    bookedMinutes = unionMinutes(bookingIntervals);
    utilisation = shiftMinutes > 0 ? Math.min(1, bookedMinutes / shiftMinutes) : null;
  } else {
    bookedMinutes = unionMinutes(bookingItems.map(b => [b.startMinutes, b.endMinutes]));
  }

  return {
    items: all.slice(0, Math.max(0, maxItems)),
    allItems: all,
    moreCount: Math.max(0, all.length - maxItems),
    bookingCount: bookingItems.length,
    eventCount: eventItems.length,
    bookedMinutes,
    shiftMinutes,
    utilisation,
    isHoliday,
    holidayLabel: isHoliday ? holidayLabel(holidays[0].raw?.list?.[0]?.info || { title: holidays[0].title, leaveType: holidays[0].leaveType }) : null,
    isOff: !isWorking && !isHoliday,
    isWorking,
  };
}

/**
 * Totals across the in-month days, for the header line
 * ("42 bookings · 68% booked").
 */
export function summariseMonth(grid, summaries) {
  let bookings = 0;
  let booked = 0;
  let shift = 0;
  grid.days.forEach((d) => {
    if (!d.inMonth) return;
    const s = summaries[d.key];
    if (!s) return;
    bookings += s.bookingCount;
    if (s.utilisation != null) {
      booked += Math.min(s.bookedMinutes, s.shiftMinutes);
      shift += s.shiftMinutes;
    }
  });
  return { bookings, utilisation: shift > 0 ? booked / shift : null };
}

/**
 * A service colour mixed toward white, for the soft fill behind a booking
 * line (the bold colour stays on its left edge). Non-hex colours pass through.
 */
export function tint(colour, amount = 0.35) {
  const m = /^#?([0-9a-f]{3}|[0-9a-f]{6})$/i.exec(String(colour || '').trim());
  if (!m) return colour;
  let hex = m[1];
  if (hex.length === 3) hex = hex.split('').map(c => c + c).join('');
  const n = parseInt(hex, 16);
  const mix = c => Math.round(c * amount + 255 * (1 - amount));
  const r = mix((n >> 16) & 255);
  const g = mix((n >> 8) & 255);
  const b = mix(n & 255);
  return `rgb(${r}, ${g}, ${b})`;
}

/**
 * The diary API refuses ranges over 31 days, and a month grid can be 42.
 * Split [startDate, endDate] (inclusive) into consecutive chunks of at most
 * `maxDays` days.
 */
export function splitDateRange(startDate, endDate, maxDays = 28) {
  const out = [];
  const end = moment(endDate, 'YYYY-MM-DD');
  let cur = moment(startDate, 'YYYY-MM-DD');
  while (!cur.isAfter(end, 'day')) {
    const chunkEnd = moment.min(cur.clone().add(maxDays - 1, 'days'), end);
    out.push({ startDate: cur.format('YYYY-MM-DD'), endDate: chunkEnd.format('YYYY-MM-DD') });
    cur = chunkEnd.clone().add(1, 'day');
  }
  return out;
}
